import { type AnonymousUserId, useUserStore } from '@/plugins/pinia/userStore.ts';
import { track, type TrackAction, type TrackTarget } from '@/api';

export function useTracking() {
  const userStore = useUserStore();

  function signUuid(uuid: AnonymousUserId): string {
    const upperUuid = uuid.toUpperCase();
    let h = 0;
    for (let i = 0; i < upperUuid.length; i++) {
      h = (h << 5) - h + upperUuid.charCodeAt(i);
      h |= 0;
    }
    const ts = Math.floor(Date.now() / 1000);
    const r = BigInt(h) + BigInt(ts);
    const ds = r.toString();
    return btoa(ds);
  }

  async function trackPageView(target: TrackTarget, targetId: number): Promise<void> {
    if (import.meta.env.SSR) {
      return;
    }
    let action: TrackAction = 'pageView';
    let uuid = userStore.uuid;
    if (!uuid) {
      uuid = userStore.newUuid();
      action = 'visit';
    }
    const signature = signUuid(uuid);
    await track({
      keepalive: true,
      body: {
        id: uuid as string,
        target: target,
        targetId: targetId,
        action: action,
        signature: signature,
      },
    });
  }

  async function trackAction(target: TrackTarget, targetId: number, action: 'website' | 'phoneClick' | 'print' | 'share' | 'mapClick' | 'homeHeroClick'): Promise<void> {
    if (import.meta.env.SSR) {
      return;
    }
    let uuid = userStore.uuid;
    let newVisit = false;
    if (!uuid) {
      uuid = userStore.newUuid();
      newVisit = true;
    }
    const signature = signUuid(uuid);
    if (newVisit)
      await track({
        keepalive: true,
        body: {
          id: uuid as string,
          target: target,
          targetId: targetId,
          action: 'visit',
          signature: signature,
        },
      });
    await track({
      keepalive: true,
      body: {
        id: uuid as string,
        target: target,
        targetId: targetId,
        action: action,
        signature: signature,
      },
    });
  }

  // Un affichage n'est pas une visite : il est signé avec l'identifiant du visiteur s'il existe, sinon avec un identifiant
  // jetable, pour ne pas créer de visiteur ni consommer la première visite d'une page de projet.
  async function trackImpression(target: TrackTarget, targetId: number, action: 'homeHeroImpression'): Promise<void> {
    if (import.meta.env.SSR) {
      return;
    }
    const uuid = userStore.uuid ?? (crypto.randomUUID() as AnonymousUserId);
    await track({
      keepalive: true,
      body: {
        id: uuid as string,
        target: target,
        targetId: targetId,
        action: action,
        signature: signUuid(uuid),
      },
    });
  }

  async function trackArticle(articleId: number, action: 'pageView' | 'projectClick' | 'share'): Promise<void> {
    if (import.meta.env.SSR) {
      return;
    }
    const uuid = userStore.uuid ?? (crypto.randomUUID() as AnonymousUserId);
    await track({
      keepalive: true,
      body: {
        id: uuid as string,
        target: 'article',
        targetId: articleId,
        action: action,
        signature: signUuid(uuid),
      },
    });
  }

  return {
    trackPageView,
    trackAction,
    trackImpression,
    trackArticle,
  };
}
